import { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProfileProvider, useProfile } from './context/ProfileContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import { initStatusBar, hideSplash } from './lib/native';
import { initBreathingFavicon } from './lib/breathingFavicon';
import Navbar from './components/Navbar';
import BootSplash from './components/BootSplash';
import DragonScaleBackdrop from './components/DragonScaleBackdrop';
import Footer from './components/Footer';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import OfflineFailureBanner from './components/OfflineFailureBanner';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Strength from './pages/Strength';
import Library from './pages/Library';
import ActivityDetail from './pages/ActivityDetail';
import Onboarding from './pages/Onboarding';
import Pricing from './pages/Pricing';
import Profile from './pages/Profile';
import Endurance from './pages/Endurance';
import Merch from './pages/Merch';
import ImageManifest from './pages/ImageManifest';
import Training from './pages/Training';
import ExerciseDetail from './pages/ExerciseDetail';
import Impact from './pages/Impact';
import Nutrition from './pages/Nutrition';
import Roadmap from './pages/Roadmap';
import Story from './pages/Story';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import SignInCode from './pages/SignInCode';
import Admin from './pages/Admin';
import Coach from './pages/Coach';
import LoaderPreview from './pages/LoaderPreview';
import DisplayPrefs from './pages/DisplayPrefs';
import PhantomLimb from './pages/PhantomLimb';
import AccountRestore from './pages/AccountRestore';
import PaymentsReturn from './pages/PaymentsReturn';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import NotFound from './pages/NotFound';
import WelcomeFromShare from './pages/WelcomeFromShare';
import FeedbackWidget from './components/FeedbackWidget';
import FloatingCoach from './components/FloatingCoach';
import { Toaster } from './components/ui/sonner';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Auth wall: any "app" route requires a signed-in user.
// Onboarding completion is NO LONGER a hard gate — pages handle the
// incomplete-profile state inline with honest empty data + contextual CTAs.
// This is the Session 4a rework.
const AuthGated = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
};

// Routes /, /login, /register all render Home — but if the user is already
// signed in, we redirect them straight to /progress so they don't have to
// tap through a landing they don't need. /login and /register are kept as
// real routes (not just modal triggers) so AuthGated redirects + deep-links
// + back-button history all continue to work.
const HomeRoute = ({ authView = null }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/progress" replace />;
  return <Home authView={authView} />;
};

const AppShell = () => {
  const { pathname } = useLocation();
  const { setDisplayPreset, loaded } = useProfile();
  const hideChrome = pathname === '/onboarding';

  // Pages where the ambient dragon-scale background is suppressed — only
  // the marketing landing has its own intentional design treatment.
  // (BootSplash separately suppresses via its own class while mounted.)
  // Per user direction: auth pages, onboarding, and Quickstart
  // should ALSO show the dragon scales — they're the primary first-impression
  // surfaces post-data-wipe.
  const noDragonScales = (pathname === '/');
  useEffect(() => {
    if (noDragonScales) document.body.classList.add('no-dragon-scales');
    else document.body.classList.remove('no-dragon-scales');
    return () => document.body.classList.remove('no-dragon-scales');
  }, [noDragonScales]);

  // Native-only: configure status bar & dismiss splash once React has mounted.
  // No-ops on web.
  useEffect(() => {
    initStatusBar();
    hideSplash();
    initBreathingFavicon();
  }, []);

  // Demo-flag: append ?simplified=1 to drop the current user into Simplified Mode
  // (useful for board / consultant walkthroughs). Persists until they opt out.
  useEffect(() => {
    if (!loaded) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('simplified') === '1') setDisplayPreset('simplified');
    } catch { /* ignore */ }
  }, [loaded, setDisplayPreset]);

  return (
    <>
      <BootSplash />
      <DragonScaleBackdrop />
      <ScrollToTop />
      {!hideChrome && <Navbar />}
      {!hideChrome && <OfflineFailureBanner />}
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/login" element={<HomeRoute authView="signin" />} />
        <Route path="/register" element={<HomeRoute authView="register" />} />
        <Route path="/story" element={<Story />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/sign-in-code" element={<SignInCode />} />
        <Route path="/onboarding" element={<AuthGated><Onboarding scope="quickstart" /></AuthGated>} />
        <Route path="/onboarding/full" element={<AuthGated><Onboarding scope="full" /></AuthGated>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/dashboard" element={<Navigate to="/progress" replace />} />
        <Route path="/progress" element={<AuthGated><Dashboard /></AuthGated>} />
        <Route path="/strength" element={<AuthGated><Strength /></AuthGated>} />
        <Route path="/adaptive/phantom-limb" element={<PhantomLimb />} />
        <Route path="/account/restore" element={<AccountRestore />} />
        <Route path="/payments/return" element={<AuthGated><PaymentsReturn /></AuthGated>} />
        <Route path="/rides" element={<Navigate to="/endurance" replace />} />
        <Route path="/library" element={<Library />} />
        <Route path="/exercise/:id" element={<ExerciseDetail />} />
        <Route path="/profile" element={<AuthGated><Profile /></AuthGated>} />
        <Route path="/recovery" element={<AuthGated><Endurance /></AuthGated>} />
        <Route path="/endurance" element={<AuthGated><Endurance /></AuthGated>} />
        <Route path="/merch" element={<Merch />} />
        <Route path="/admin/image-manifest" element={<AuthGated><ImageManifest /></AuthGated>} />
        <Route path="/training" element={<AuthGated><Training /></AuthGated>} />
        <Route path="/nutrition" element={<AuthGated><Nutrition /></AuthGated>} />
        <Route path="/impact" element={<Impact />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/coach" element={<Coach />} />
        <Route path="/loader-preview" element={<LoaderPreview />} />
        <Route path="/profile/display" element={<AuthGated><DisplayPrefs /></AuthGated>} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/activity/:id" element={<AuthGated><ActivityDetail /></AuthGated>} />
        <Route path="/legal/privacy" element={<Privacy />} />
        <Route path="/legal/terms" element={<Terms />} />
        <Route path="/welcome/:context" element={<WelcomeFromShare />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!hideChrome && <Footer />}
      {!hideChrome && <FeedbackWidget />}
      <FloatingCoach />
      <UpdateAvailableBanner />
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <FeatureFlagsProvider>
          <AuthProvider>
            <ProfileProvider>
              <AppShell />
            </ProfileProvider>
          </AuthProvider>
        </FeatureFlagsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
